import type { ScenarioId, SessionConfig } from '@friction/engine';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles/fonts';
import './styles/tokens.css';
import './styles/global.css';
import { App } from './app/App';
import { initAudio } from './audio';
import { installChronicle } from './game/chronicle';

initAudio();
installChronicle();

// Dev fixtures (?fixture=game|gameover|lobby|rooms) — see src/dev/fixtures.ts.
const fixture = new URLSearchParams(location.search).get('fixture');
if (import.meta.env.DEV && fixture) {
  const params = new URLSearchParams(location.search);
  void import('./dev/fixtures').then((m) => m.installFixture(fixture, params));
}

// Dev-only handle for debugging and automated browser checks (never shipped in production builds).
if (import.meta.env.DEV) {
  void Promise.all([
    import('./game/store'),
    import('./net/lobby'),
    import('./replay/store'),
    import('./app/settings'),
    import('./app/nav'),
    import('./dev/layoutCheck'),
    import('./ui/game/hudStore'),
  ]).then(([game, lobby, replay, settings, nav, probe, hud]) => {
    // settings/nav/hud/layoutCheck: switch language, screen and drawer, probe for clipped text in
    // automated localisation checks (docs/Localization.md).
    Object.assign(window, {
      __friction: {
        game: game.gameStore,
        lobby: lobby.lobbyStore,
        replay: replay.replayStore,
        settings: settings.settingsStore,
        nav: nav.navStore,
        hud: hud.hudStore,
        layoutCheck: probe.layoutCheck,
      },
    });
    // ?quickstart[=seed] jumps straight into a medium single-player match against two AIs
    // (&theatre=poland-1939[&nation=1] on a real-world map, &scenario=enigma for a preset);
    // &days=N ends it at a day limit and &speed=1..5 sets the clock (quick finished wars, e.g.
    // to test "The Fog Lifts" replay: ?quickstart=1924&days=4&speed=5); &season= and &weather=
    // for weather checks (?quickstart=7&season=autumn&weather=storm).
    const params = new URLSearchParams(location.search);
    // ?academy[=seed] opens the Kriegsakademie directly.
    const academy = params.get('academy');
    if (academy !== null)
      void import('./tutorial/launch').then((t) => t.startAcademy(Number(academy) || undefined));
    const quick = params.get('quickstart');
    if (quick !== null)
      void Promise.all([import('./dev/weather'), import('@friction/engine')]).then(
        ([{ devWeatherParams }, engine]) => {
          const { isTheatreId, SCENARIO_IDS, scenarioConfig, THEATRES } = engine;
          // &season=… picks the campaign's season, &weather=… pins the sky (see dev/weather.ts).
          const { season, weather } = devWeatherParams(params);
          // &theatre=<id> plays a real-world theatre (you command nation &nation=, default 0);
          // &scenario=<id> starts a scenario preset.
          const seed = Number(quick) || 1924;
          const theatre = params.get('theatre');
          const scenario = params.get('scenario');
          const nations = theatre && isTheatreId(theatre) ? THEATRES[theatre].nations.length : 3;
          const me = Number(params.get('nation')) || 0;
          const base: SessionConfig =
            scenario && (SCENARIO_IDS as string[]).includes(scenario)
              ? scenarioConfig(scenario as ScenarioId, seed)
              : {
                  seed,
                  mapSize: 'medium',
                  ...(theatre && isTheatreId(theatre) ? { theatre } : {}),
                  factions: Array.from({ length: nations }, (_, i) =>
                    i === me
                      ? { controller: 'human' as const }
                      : { controller: 'ai' as const, aiDifficulty: 'veteran' as const },
                  ),
                  dayLimit: 0,
                  vpVictoryShare: 0.6,
                };
          lobby.startSinglePlayer(
            {
              ...base,
              dayLimit: Number(params.get('days')) || base.dayLimit,
              ...(season ? { season } : {}),
            },
            Math.min(5, Math.max(1, Number(params.get('speed')) || 2)) as 1 | 2 | 3 | 4 | 5,
            { devWeather: weather },
          );
        },
      );
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
